import type { ComplianceSummary, MonthlyCompliance } from "../types";
import { windowLong, windowShort } from "../lib/reportingWindow";

interface Props {
  summary: ComplianceSummary;
}

const TARGET_PCT = 80;

function pctTextColor(pct: number): string {
  if (pct >= TARGET_PCT) return "text-emerald-600";
  if (pct >= TARGET_PCT - 10) return "text-amber-600";
  return "text-red-600";
}

function bgForPct(pct: number): string {
  if (pct >= TARGET_PCT) return "bg-emerald-500";
  if (pct >= TARGET_PCT - 10) return "bg-amber-400";
  return "bg-red-500";
}

// Pillar rate over the completed months in the window, summed the same
// way the Compliance trend header does (MonthEndChart). Months where the
// pillar wasn't evaluable (pct null, e.g. approval still pending) are
// skipped. Returns null when nothing was evaluable.
function pillarPct(
  monthly: MonthlyCompliance[],
  passedKey: keyof MonthlyCompliance,
  evaluatedKey: keyof MonthlyCompliance,
  pctKey: keyof MonthlyCompliance,
): number | null {
  let passed = 0;
  let evaluated = 0;
  for (const m of monthly) {
    if (m[pctKey] === null || m[pctKey] === undefined) continue;
    passed += Number(m[passedKey] ?? 0);
    evaluated += Number(m[evaluatedKey] ?? 0);
  }
  return evaluated > 0 ? (100 * passed) / evaluated : null;
}

export default function KpiCards({ summary }: Props) {
  const { kpis, ytd_weeks } = summary;
  const monthly = summary.monthly_compliance ?? [];

  // Headline = average of the four pillar rates, using the same
  // definitions as the Compliance trend and the department tiles.
  const pillars = [
    pillarPct(monthly, "weekly_on_time", "weekly_total", "weekly_pct"),
    pillarPct(monthly, "month_end_passed", "month_end_evaluated", "month_end_pct"),
    pillarPct(monthly, "approval_passed", "approval_evaluated", "approval_pct"),
    pillarPct(monthly, "hours_passed", "hours_evaluated", "hours_pct"),
  ].filter((v): v is number => v !== null);

  // Round to the displayed precision before coloring so the color and
  // the label always agree. Falls back to the clean-weeks KPI for older
  // payloads without monthly data.
  const overall =
    pillars.length > 0
      ? Math.round((10 * pillars.reduce((a, b) => a + b, 0)) / pillars.length) / 10
      : kpis.company_ytd_pct;
  const targetGap = TARGET_PCT - overall;
  const onTrack = overall >= TARGET_PCT;
  const clean = kpis.company_ytd_pct;

  return (
    <section className="space-y-4">
      <div className="relative rounded-2xl bg-white p-6 shadow-card border border-slate-200/70 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-end">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-rancho-slate">
              Overall compliance · {windowShort(summary)}
            </div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className={`text-6xl font-extrabold tracking-tight ${pctTextColor(overall)}`}>
                {overall.toFixed(1)}%
              </div>
              <div className="flex items-baseline gap-1 text-sm text-rancho-slate">
                <span>vs</span>
                <span className="font-bold text-rancho-ink">{TARGET_PCT}%</span>
                <span>target</span>
              </div>
            </div>
            <div className="mt-1 text-xs text-rancho-slate">
              Average of the four pillar rates - weekly submission, month-end submission, team-lead approval, and hours worked - across {ytd_weeks} completed week{ytd_weeks === 1 ? "" : "s"} {windowLong(summary)} ({kpis.employee_count} people), using the same definitions as the Compliance trend below.
            </div>

            <div className="mt-4 relative">
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${bgForPct(overall)} transition-all`}
                  style={{ width: `${Math.max(2, overall)}%` }}
                />
              </div>
              <div
                className="absolute top-0 bottom-0 border-l-2 border-dashed border-slate-700"
                style={{ left: `${TARGET_PCT}%` }}
                aria-hidden
              />
            </div>

            <div
              className="mt-3 text-xs text-rancho-slate"
              title="Share of person-weeks that passed all four pillars in the same week. One failed check fails the whole week, so this sits below the weakest pillar."
            >
              Clean weeks (all 4 pillars in the same week):{" "}
              <span className="font-semibold text-rancho-ink">{clean.toFixed(1)}%</span>
            </div>
          </div>

          <div className="md:text-right">
            <div
              className={
                "inline-block rounded-pill px-4 py-2 text-sm font-bold " +
                (onTrack
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-red-100 text-red-700")
              }
            >
              {onTrack ? "ON TARGET" : `${Math.abs(targetGap).toFixed(1)}% BELOW TARGET`}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
